import { ApiResponse, GlobalStore, UserStore } from '@roc/feature-app-core';
import {
  ELMSURE,
  bridgeLoanSubTypes,
  GENERIC_ERROR_MESSAGE,
  LoanSubType,
  PURCHASE,
  REFINANCE,
  isFixAndFlip,
  isGroundUp,
  isNil,
  isNotBlank,
  isStabilizedBridge,
  LoanType,
  getDaysBetween,
  MIXED_USE,
  MULTIFAMILY_5_PLUS,
  borrowerExpMapping,
  TERM,
  SECOND_APPRAISAL_FULL,
  TWO_PAYMENTS,
  ONE_PAYMENT,
  Roles,
} from '@roc/feature-utils';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { OneToolService } from '../services/oneToolService';
import { PreflightStep, QuoteStep, quoteSteps, singlePropertyRentalQuoteSteps, singlePropertyRentalQuoteStepsWithoutPricer, SelectEntityOption, SecondAppraisalDetails, IsSecondAppraisalRequiredRequest, SecondAppraisalPricingDataRequest, SecondAppraisalPricingData, BridgeProductBox, correspondentBetaAllowedRoles, correspondentBetaLoanSubtypes } from '../utils/constants';
import { BorrowersStore } from './borrowers/borrowersStore';
import { LoanEconomicsStore } from './loanEconomics/loanEconomicsStore';
import { PropertiesStore } from './properties/propertiesStore';
import { SummaryStore } from './summary/summaryStore';
import { PreflightStore } from './prefight/preflightStore';
import { BrokerStore } from './broker/brokerStore';
import { EntityStore } from './entity/entityStore';
import { ClosingStore } from '../stores/closing/closingStore';
import { getDealName, LoanService } from '@roc/feature-loans';
import { DSPropertyService } from '@roc/feature-loans';
import { buildAddress, getQuoteSteps, sum } from '../utils/utils';
import { differenceInYears, parse, startOfToday } from 'date-fns';
import PricerStore from './singlePropertyPricer/pricerStore';
import PricerSummaryStore from './singlePropertyPricer/pricerSummaryStore';
import { DscrCalculatorStore } from 'libs/client-portal-shared/src/app/modules/dscrCalculator/stores/dscrCalculatorStore';
import LoanDetailsStore from './singlePropertyPricer/loanDetailsStore';
import { PortfolioInformationFormStore } from './portfolioInformation/portfolioInformationFormStore';
import { LoanQuoteData } from '../types/LoanQuoteData';
import { mapNumberOfUnits } from 'libs/client-portal-shared/src/app/modules/internalPricer/utils/internalPricerUtils';
import { CustomQuoteRequest } from '@roc/feature-types';

const TYPICAL_VALUE_LOWER_LIMIT = 750000;
const TYPICAL_VALUE_UPPER_LIMIT = 1500000;

export abstract class QuoteTypeStore {
  public globalStore: GlobalStore;
  userStore: UserStore;

  private oneToolSevice: OneToolService;
  private loanService: LoanService;
  private dsPropertyService: DSPropertyService;

  brokerStore: BrokerStore;
  entityStore: EntityStore;
  borrowersStore: BorrowersStore;
  propertiesStore: PropertiesStore;
  portfolioInformationFormStore: PortfolioInformationFormStore;
  loanEconomicsStore: LoanEconomicsStore;
  pricerStore: PricerStore;
  pricerSummaryStore: PricerSummaryStore;
  loanDetailsStore: LoanDetailsStore;
  dscrCalculatorStore: DscrCalculatorStore;
  closingStore: ClosingStore;
  summaryStore: SummaryStore;
  preflightStore: PreflightStore;
  fastTrackFees: any;
  fastTrackLoanId: string | number;
  fastTrackPaymentUrl: string;

  loanSubtype: LoanSubType;

  step: QuoteStep;
  lastCompletedStep: QuoteStep;
  secondAppraisal: SecondAppraisalDetails;
  secondAppraisalPricingData: SecondAppraisalPricingData;

  quoteId: number;
  loanId: number;
  lenderId: number;

  isEditingLoan: boolean;
  hasCorrespondentBeta: boolean;

  quoteAccepted: boolean;

  submittedLoan;

  allErrors: string[];
  allWarnings: string[];
  stepErrors: string[];

  isLoanQuoteDetailsSuccess: boolean;
  areBorrowersPreQualified: boolean;
  disableSubmitLoanButton: boolean;
  allCounties: any;

  internalValuationPilotDialogOpen: boolean;
  internalValuationPilotSelected: boolean | null;
  internalValuationPilotApplicable: boolean;
  internalValuationPilotFeeName: string;
  variantYesButton: 'contained' | 'outlined';
  variantNoButton: 'contained' | 'outlined';

  medianZipCodeValueMap: Map<string, number>;

  suggestedLoanQuote: any;

  constructor(globalStore: GlobalStore, userStore: UserStore) {
    this.globalStore = globalStore;
    this.userStore = userStore;
    this.borrowersStore = new BorrowersStore(this.globalStore, this);
    this.closingStore = new ClosingStore(this.globalStore, this);
    this.summaryStore = new SummaryStore(this.globalStore, this);
    this.preflightStore = new PreflightStore(this.globalStore, this);
    this.brokerStore = new BrokerStore(globalStore, userStore);
    this.entityStore = new EntityStore(globalStore, this);
    this.oneToolSevice = new OneToolService();
    this.loanService = new LoanService();
    this.dscrCalculatorStore = new DscrCalculatorStore(globalStore);
    this.loanDetailsStore = new LoanDetailsStore(globalStore, this);
    this.pricerSummaryStore = new PricerSummaryStore(globalStore, this, userStore);
    this.portfolioInformationFormStore = new PortfolioInformationFormStore(globalStore);
    this.dsPropertyService = new DSPropertyService();
    this.setDefaults();

    this.allErrors = [];
    this.allWarnings = [];
    this.allCounties = [];

    makeObservable(this, {
      loanSubtype: observable,
      step: observable,
      quoteId: observable,
      submittedLoan: observable,
      allErrors: observable,
      allWarnings: observable,
      stepErrors: observable,
      reset: action,
      goToStep: flow,
      validateCurrentStep: action,
      saveQuote: flow,
      saveQuoteBeforeSubmit: flow,
      saveSuggestedQuoteIfEligible: flow,
      getSuggestedQuote: flow,
      checkIfLoanApplicableForIV: flow,
      loadQuote: flow,
      loadQuoteToEdit: flow,
      loadManualQuoteData: flow,
      createNewQuote: flow,
      getNumberOfUnits: action,
      getNumberOfProperties: action,
      getPropertyTypes: action,
      getPropertyRentalPortfolioTypes: action,
      getPropertyCounties: action,
      getPropertyTypesAsString: action,
      getQuoteData: action,
      getLeverageMatrixData: action,
      getRateType: action,
      getPropertyDataPortfolio: action,
      doSubmitLoanValidation: flow,
      submitLoan: flow,
      submitLoanApplication: flow,
      isManualLeverageRequired: action,
      fastTrackFees: observable,
      fastTrackLoanId: observable,
      fastTrackPaymentUrl: observable,
      loadFastTrackFees: flow,
      fetchFastTrackLoanId: flow,
      getLoanFastTrackPaymentUrl: flow,
      updateLoanFastTrackInfo: flow,
      requestFastTrackPaymentEmail: flow,
      isBridgeLoan: computed,
      validateLeverageStep: action,
      isLoanQuoteDetailsSuccess: observable,
      validateBridgeSubmission: flow,
      validateBorrowerErrors: flow,
      validateEntityErrors: flow,
      getAreBorrowersPreQualifiedFromPrescreenValidations: flow,
      areBorrowersPreQualified: observable,
      isMidConstructionRefinance: action,
      getCostBasisLabel: action,
      validateProtectedBorrowers: action,
      disableSubmitLoanButton: observable,
      isSeasoningDaysGreaterThan180: computed,
      showOnlyLTVValues: computed,
      resetSteps: action,
      getCorrectTermStep: action,
      isValidBankruptcyDate: action,
      isValidForeclosureDate: action,
      isSecondAppraisalNeeded: flow,
      secondAppraisal: observable,
      allCounties: observable,
      fetchStateCounties: flow,
      counties: computed,
      isEditingLoan: observable,
      hasCorrespondentBeta: observable,
      isCorrespondentBetaFormEnabled: computed,
      correspondentSellAs: computed,
      isWholeLoanParticipation: computed,
      updateLoan: flow,
      allowPricerStep: computed,
      internalValuationPilotDialogOpen: observable,
      internalValuationPilotApplicable: observable,
      internalValuationPilotFeeName: observable,
      internalValuationPilotSelected: observable,
      variantYesButton: observable,
      variantNoButton: observable,
      loadInternalValuationFields: action,
      setMedianZipCodeValueForProperties: flow,
      fetchMedianZipCodeValue: flow,
      saveLoanTermApprovalValue: flow,
      updateManualQuoteLoan: flow,
      suggestedLoanQuote: observable,
    });
  }

  get isBridgeLoan() {
    return bridgeLoanSubTypes.includes(this.loanSubtype);
  }

  get isCorrespondentBetaFormEnabled() {
    if (this.isEditingLoan) {
      return this.hasCorrespondentBeta;
    }
    const isEligibleLoanSubtype = correspondentBetaLoanSubtypes.includes(this.loanSubtype);
    const isAllowedRole = this.userStore.userInformation?.roles?.some(role => correspondentBetaAllowedRoles.includes(role as Roles)) ?? false;
    return this.hasCorrespondentBeta && isEligibleLoanSubtype && isAllowedRole;
  }

  get correspondentSellAs() {
    return this.isCorrespondentBetaFormEnabled
      ? this.closingStore.closingFormStore.form.fields.sellAs.value
      : undefined;
  }

  get isWholeLoanParticipation() {
    return this.correspondentSellAs === 'Whole Loan';
  }

  get showValuationCard() {
    return (
      !this.internalValuationPilotSelected &&
      this.loanSubtype !== LoanSubType.FIX_AND_FLIP_PRO &&
      !this.isEditingLoan
    );
  }

  reset() {
    this.step = QuoteStep.ENTITY_INFORMATION;
    this.lastCompletedStep = null;
    this.quoteId = null;
    this.loanId = null;
    this.isEditingLoan = null;
    //this.quoteAccepted = false;
    this.quoteAccepted = true;
    this.borrowersStore.reset();
    this.portfolioInformationFormStore.reset();
    this.propertiesStore.reset();
    this.loanEconomicsStore.reset();
    this.loanDetailsStore.reset();
    this.summaryStore.reset();
    this.preflightStore.reset();
    this.brokerStore.reset();
    this.entityStore.reset();
    this.closingStore.reset();
    this.pricerSummaryStore.resetStore();
    this.allErrors = [];
    this.allWarnings = [];
    this.isLoanQuoteDetailsSuccess = true;
    this.areBorrowersPreQualified = true;
    this.medianZipCodeValueMap = new Map<string, number>();
    this.setDefaults();
  }

  setDefaults() {
    this.lenderId = this.globalStore?.lenderInfo?.lenderId ?? null;
    this.hasCorrespondentBeta = this.globalStore?.hasCorrespondentBeta ?? false;
    this.fastTrackLoanId = '';
    this.fastTrackFees = {};
    this.fastTrackPaymentUrl = '';
    this.stepErrors = [];
    this.disableSubmitLoanButton = false;
    this.secondAppraisal = {
      appraisalReason: '',
      appraisalType: '',
      secondAppraisalRequired: false
    }
    this.secondAppraisalPricingData = {
      firstAppraisalCost: 0,
      secondAppraisalCost: 0,
      totalCost: 0
    }
    this.internalValuationPilotDialogOpen = false;
    this.internalValuationPilotApplicable = false;
    this.internalValuationPilotSelected = null;
    this.internalValuationPilotFeeName = null;
    this.variantYesButton = 'outlined';
    this.variantNoButton = 'outlined';
    this.suggestedLoanQuote = {};
  }

  loadInternalValuationFields() {
    const loanData = this.getQuoteData();
    this.checkIfLoanApplicableForIV();
    this.variantYesButton = loanData.internalValuationPilotSelected === true ? 'contained' : 'outlined';
    this.variantNoButton = loanData.internalValuationPilotSelected === false ? 'contained' : 'outlined';
    this.internalValuationPilotFeeName = loanData.internalValuationPilotFeeName ? loanData.internalValuationPilotFeeName : 'Valuation';
  }

  *goToStep(step: QuoteStep) {
    if (step === QuoteStep.SUMMARY) {
      this.borrowersStore.turnOffBorrowersEditMode();
      this.propertiesStore.turnOffPropertiesEditMode();
    }

    const steps = this.getQuoteSteps()
    const isSinglePropertyRental = this.loanSubtype === LoanSubType.SINGLE_PROPERTY;

    if (isSinglePropertyRental && steps.indexOf(step) <= steps.indexOf(QuoteStep.LOAN_PRICER)) {
      this.pricerSummaryStore.submittedQuote = null;
      this.pricerSummaryStore.submittedPricingOption = null;
    }

    if (steps.indexOf(step) < steps.indexOf(this.step)) {
      this.step = step;
    } else if (this.validateCurrentStep()) {
      if (this.canGoToStep(step)) {
        this.updateLastCompletedStep();
        if (step === QuoteStep.LEVERAGE) {
          yield this.setMedianZipCodeValueForProperties();
        }
        this.step = step;
        if (!this.isEditingLoan) {
          yield this.saveQuote();
        }
      } else {
        this.globalStore.notificationStore.showInfoNotification({
          message: 'Please fill the previous steps',
        });
      }
    }
  }

  // Helper to fetch and cache median value for a zip code
  *fetchMedianZipCodeValue(zipCode: string) {
    if (!zipCode) return null;
    if (this.medianZipCodeValueMap.has(zipCode)) {
      return { medianPrice: this.medianZipCodeValueMap.get(zipCode) };
    }
    try {
      const response = yield this.dsPropertyService.getTypicalValueData(zipCode);
      const medianPrice = response?.data?.data?.medianPrice ?? null;
      this.medianZipCodeValueMap.set(zipCode, medianPrice);
      return { medianPrice };
    } catch (e) {
      console.error('Error fetching median value for zip code:', zipCode, e);
      this.medianZipCodeValueMap.set(zipCode, null);
      return { medianPrice: null };
    }
  }

  *setMedianZipCodeValueForProperties() {
    try {
      if (
        this.loanSubtype != LoanSubType.FIX_AND_FLIP &&
        this.loanSubtype != LoanSubType.FIX_AND_FLIP_PRO &&
        this.loanSubtype != LoanSubType.GROUND_UP
      ) {
        return;
      }

      // Collect all zip codes from propertyFormStores and deduplicate
      const propertyFormStores = this.propertiesStore.propertyFormStores;
      const zipCodes = propertyFormStores.map(store => store.form.fields.zipCode.value);
      const uniqueZipCodes = Array.from(new Set(zipCodes.filter(Boolean)));

      // Fetch all typical value data for unique zip codes in parallel using Promise.all and cache
      const responses = yield Promise.all(
        uniqueZipCodes.map(zipCode => this.fetchMedianZipCodeValue(zipCode))
      );

      // Map zip code to median price
      const zipCodeResponses = {};
      uniqueZipCodes.forEach((zipCode, idx) => {
        zipCodeResponses[zipCode] = responses[idx]?.medianPrice ?? null;
      });

      // Assign each response to the corresponding stores
      // medianZipCodeValue field by matching zip code
      propertyFormStores.forEach(store => {
        const zipCode = store.form.fields.zipCode.value;
        store.onFieldChange('medianZipCodeValue', zipCodeResponses[zipCode]);
      });
    } catch (e) {
      this.propertiesStore.propertyFormStores.forEach(store => {
        store.onFieldChange('medianZipCodeValue', null);
      });
      console.error('Error setting median zip code value for:', e);
    }
  }

  private canGoToStep(step: QuoteStep) {
    const steps = this.getQuoteSteps();
    return (
      steps.indexOf(step) <= steps.indexOf(this.lastCompletedStep) + 1 ||
      steps.indexOf(step) === steps.indexOf(this.step) + 1
    );
  }


  private updateLastCompletedStep() {
    const isSinglePropertyRental = this.loanSubtype === LoanSubType.SINGLE_PROPERTY;
    const steps = isSinglePropertyRental ? singlePropertyRentalQuoteSteps : this.getQuoteSteps();
    if (steps.indexOf(this.lastCompletedStep) < steps.indexOf(this.step)) {
      this.lastCompletedStep = this.step;
    }
  }

  private getQuoteSteps() {
    return getQuoteSteps(this.loanSubtype, this.allowPricerStep);
  }

  private checkAllErrors = () => {
    this.allErrors = [];

    const pointsError = this.checkPoints();
    if (pointsError) this.allErrors.push(pointsError);

    if (this.entityStore?.getEntity()?.hasEntity === null) {
      this.allErrors.push(
        'Please answer "Do you have the borrowing entity information?" under Entity section.'
      );
    }

    if (this.borrowersStore?.getBorrowers()?.length === 0) {
      this.allErrors.push('Please select a borrower or add a new one.');
    }

    this.checkBorrowersErrors();

    if (this.propertiesStore?.getProperties()?.length === 0) {
      this.allErrors.push('Please add a new property.');
    }

    const totalLoanAmount = this.loanEconomicsStore?.enableManualQuote
      ? this.loanEconomicsStore?.manualExceptionQuoteFormStore?.calculatedFields
        ?.totalLoanAmount
      : this.loanEconomicsStore?.instaLeverageQuoteFormStore?.calculatedFields
        ?.totalLoanAmount;
    if (totalLoanAmount === 0) {
      this.allErrors.push('Total loan amount must be greater than 0.');
    }

    if (
      this.closingStore.closingFormStore.getFormValues().preferredTitle ===
      'Other'
    ) {
      this.closingStore.closingFormStore.runFormValidation();
      if (!this.closingStore.closingFormStore.form.meta.isValid) {
        this.allErrors.push(
          'Please fill out all the info for Preferred Title Company.'
        );
      }

      const allowLenderUnderwritingFee = this.globalStore.userFeatures
        ?.allowLenderUnderwritingFee;
      if (allowLenderUnderwritingFee /* && this.userStore.allowLoanPricing */) {
        const error = this.checkLenderUnderwritingFeeValidations();
        if (error) {
          this.allErrors.push(error);
        }
      }
    }
  };

  private checkAllWarnings = () => {
    this.allWarnings = [];

    this.checkBorrowersWarnings();

    const entity = this.entityStore?.getEntity();
    if (entity) {
      if (entity.dateOfIncorporation) {
        const diff = differenceInYears(
          startOfToday(),
          parse(
            this.entityStore?.getEntity()?.dateOfIncorporation,
            'MM/dd/yyyy',
            new Date()
          )
        );
        if (diff > 1) {
          this.allWarnings.push(
            'Certificate of Good Standing is needed within 90 days of closing. These can be ordered on the applicable county/state website'
          );
        }
      }
      if (
        (isNotBlank(entity.entityName) || !isNil(entity.entityEin)) &&
        this.propertiesStore
          ?.getProperties()
          .some(p => p.state !== entity.address?.state)
      ) {
        this.allWarnings.push(
          'The borrower must obtain a foreign entity status in the state of the collateral. These can be ordered on the applicable county/state website'
        );
      }
    }

    // Sundar: This validation is from old loan submission. Commenting since multifamily bridge is not there in one tool
    /* if (this.isMultifamilyBridge && this.isConstructionHoldbackAndLTFC()) {
      this.allWarnings.push(
        'The loan is lopsided (Initial loan amount < Construction Holdback) and over 93% of cost. The initial loan to cost is too high.'
      );
    } */
  };

  private checkPoints() {
    const brokerPoints = this.brokerStore.getBroker().brokerPoints;
    const loanEconomicsFormValues = this.loanEconomicsStore.loanEconomicsFormStore.getFormValues();
    const lenderPoints = this.globalStore?.lenderInfo?.isInternal
      ? loanEconomicsFormValues.capitalProviderPoints || 0
      : (loanEconomicsFormValues.lenderPoints || 0) +
      (loanEconomicsFormValues.capitalProviderPoints || 0);
    return brokerPoints > lenderPoints
      ? 'Broker points cannot exceed total origination points.'
      : '';
  }

  private checkLenderUnderwritingFeeValidations() {
    if (this.loanSubtype === LoanSubType.FIX_AND_FLIP_PRO) {
      return '';
    }
    const totalLoanAmount = this.loanEconomicsStore.enableManualQuote
      ? this.loanEconomicsStore.manualExceptionQuoteFormStore?.calculatedFields
        ?.totalLoanAmount
      : this.loanEconomicsStore.instaLeverageQuoteFormStore?.calculatedFields
        ?.totalLoanAmount;
    let totalFees = 0;
    this.loanEconomicsStore.feeStores.forEach(feeStore => {
      totalFees += feeStore.form.fields.capitalProviderAmount.value;
    });
    if (totalLoanAmount < 500000 && totalFees < 595) {
      return 'Total Fees for this loan must be greater than or equal to $595.';
    }
    if (
      totalLoanAmount >= 500000 &&
      totalLoanAmount < 1000000 &&
      totalFees < 895
    ) {
      return 'Total Fees for this loan must be greater than or equal to $895.';
    }
    if (
      totalLoanAmount >= 1000000 &&
      totalLoanAmount < 2000000 &&
      totalFees < 1295
    ) {
      return 'Total Fees for this loan must be greater than or equal to $1,295.';
    }
    if (
      totalLoanAmount >= 2000000 &&
      totalLoanAmount < 3000000 &&
      totalFees < 1995
    ) {
      return 'Total Fees for this loan must be greater than or equal to $1,995.';
    }
    if (
      totalLoanAmount >= 3000000 &&
      totalLoanAmount < 4000000 &&
      totalFees < 3495
    ) {
      return 'Total Fees for this loan must be greater than or equal to $3,495.';
    }
    if (
      totalLoanAmount >= 4000000 &&
      totalLoanAmount < 5000000 &&
      totalFees < 4495
    ) {
      return 'Total Fees for this loan must be greater than or equal to $4,495.';
    }
    return '';
  }

  private checkBorrowersErrors = () => {
    let thereIsAtLeastOnePG = false;
    this.borrowersStore?.getBorrowers()?.forEach(borrower => {
      thereIsAtLeastOnePG = thereIsAtLeastOnePG || borrower?.personalGuarantor;
    });
    if (!thereIsAtLeastOnePG) {
      this.allErrors.push(
        'At least one Borrower should be Personal Guarantor.'
      );
    }
  };

  private checkBorrowersWarnings = () => {
    if (
      !isFixAndFlip(this.loanSubtype) &&
      this.entityStore?.getEntity()?.hasEntity &&
      this.borrowersStore?.getBorrowers()?.length > 0 &&
      this.calculateBorrowerPercentages() !== 100
    ) {
      this.allWarnings.push(
        'Please ensure that "Percentage of Ownership" adds to a total of 100%. Please adjust the ownership percentage slider accordingly.'
      );
    }
  };

  private calculateBorrowerPercentages = () => {
    return this.borrowersStore
      ?.getBorrowers()
      ?.reduce(
        (total, current) => total + current?.percentageOfOwnership ?? 0,
        0
      );
  };

  *validateBridgeSubmission(submitBridgeLoanRequestData: any) {
    try {
      let response: ApiResponse;
      if (isFixAndFlip(this.loanSubtype)) {
        response = yield this.oneToolSevice.validateBridgeLoanStep(
          {
            ...submitBridgeLoanRequestData,
            internalLender: this.globalStore.lenderInfo?.isInternal,
          },
          'SUMMARY',
          this.loanEconomicsStore.enableManualQuote
        );
      } else {
        const skipLeverageValidations =
          this.loanEconomicsStore.enableManualQuote ||
          isGroundUp(this.loanSubtype);
        response = yield this.oneToolSevice.getValidateBridgeLoanErrors(
          {
            ...submitBridgeLoanRequestData,
            internalLender: this.globalStore.lenderInfo?.isInternal,
          },
          skipLeverageValidations
        );
      }
      if (response.data?.data) {
        const data = response.data.data;
        const validateErrors = data.errors || [];
        this.allErrors = [...this.allErrors, ...validateErrors];

        const validateWarnings = data.warnings || [];
        this.allWarnings = [...this.allWarnings, ...validateWarnings];
      }
    } catch (error) {
      console.log(error);
    }
  }

  *validateBorrowerErrors(quoteData: any, submitBridgeLoanRequestData: any) {
    try {
      const response: ApiResponse = yield this.oneToolSevice.getValidateBorrowerLoanErrors(
        {
          borrowerRows: [...submitBridgeLoanRequestData.borrowers],
          loanValues: {
            marketingPromotionId: quoteData.marketingPromotionId, //marketing id not yet implemented
            //loanId: this.editLoanJson.loanId // edit loan not yet implemented
          },
        }
      );
      if (response.data?.data) {
        const data = response.data.data;
        const validateErrors = data.errors || [];
        this.allErrors = [...this.allErrors, ...validateErrors];

        const validateWarnings = data.warnings || [];
        this.allWarnings = [...this.allWarnings, ...validateWarnings];
      }
    } catch (error) {
      console.log(error);
    }
  }

  *validateEntityErrors(submitBridgeLoanRequestData: any) {
    try {
      const entityId = submitBridgeLoanRequestData?.entity?.borrowerEntityId;
      if (!entityId) return;

      const response: ApiResponse = yield this.oneToolSevice.getValidateEntityLoanErrors(
        entityId
      );
      if (response.data?.data) {
        const data = response.data.data;
        const validateErrors = data.errors || [];
        this.allErrors = [...this.allErrors, ...validateErrors];
      }
    } catch (error) {
      console.log(error);
    }
  }

  *getAreBorrowersPreQualifiedFromPrescreenValidations(
    submitBridgeLoanRequestData: any
  ) {
    try {
      this.areBorrowersPreQualified = false;

      const response = yield this.oneToolSevice.getAreBorrowersPreQualifiedFromPrescreenValidations(
        submitBridgeLoanRequestData
      );

      if (response.data?.data) {
        this.areBorrowersPreQualified = response?.data?.data;

        const validateWarnings = [];
        if (!this.areBorrowersPreQualified) {
          validateWarnings.push(
            'Your loan will be automatically assigned to our underwriting team once borrower credit and background are received.'
          );
        }
        this.allWarnings = [...this.allWarnings, ...validateWarnings];
      }
    } catch (error) {
      console.log(error);
    }
  }

  validateProtectedBorrowers(quoteData) {
    try {
      if (quoteData?.borrowers != null && quoteData?.borrowers.length > 0) {
        const validateWarnings = [];
        quoteData?.borrowers.map(borrower => {
          if (borrower?.protectedBorrower) {
            validateWarnings.push(
              'The Borrower ' +
              borrower.fullName +
              ' currently has loans associated with an active TPO'
            );
          }
        });
        this.allWarnings = [...this.allWarnings, ...validateWarnings];
      }
    } catch (error) {
      console.log(error);
    }
  }

  validateCurrentStep() {
    this.stepErrors = [];
    switch (this.step) {
      case QuoteStep.ENTITY_INFORMATION:
        return this.entityStore.validateEntity();
      case QuoteStep.BORROWER_INFORMATION:
        return this.borrowersStore.validateAllBorrowers();
      case QuoteStep.PROPERTY_INFORMATION:
        return this.propertiesStore.validateAllProperties();
      case QuoteStep.PORTFOLIO_INFORMATION:
        return this.portfolioInformationFormStore.validateAndCleanupUnusedFields();
      case QuoteStep.LOAN_ECONOMICS:
        if (this.internalValuationPilotApplicable && !this.isEditingLoan && this.internalValuationPilotSelected === null) {
          this.stepErrors = ['Please select Yes or No for the RealVal eligibility question before proceeding.'];
          this.globalStore.notificationStore.showErrorNotification({
            message: 'Please select Yes or No for the RealVal eligibility question before proceeding.',
          });
          return false;
        }
        if (this.userStore.allowLoanPricing) {
          return (
            this.loanEconomicsStore.validateAllFees() &&
            this.brokerStore.validateBroker_quote()
          );
        } else {
          return this.loanEconomicsStore.validateAllFeesJrOrig();
        }
      case QuoteStep.SUMMARY:
        return this.summaryStore.validateSummaryStep();
      case QuoteStep.LEVERAGE:
        return this.validateLeverageStep();
      default:
        return true;
    }
  }

  *saveQuote() {
    try {
      yield this.brokerStore.getCurrentBroker();
      const response = yield this.oneToolSevice.saveQuote(this.getQuoteData());
      this.quoteId = response.data.data.quoteId;
      return true;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      return false;
    }
  }

  // The backend reads the valuation questionnaire from the saved quote, not from the
  // submit payload, so anything changed since the last autosave (e.g. the AMC picked
  // on the Appraisal step) has to be saved before submitting or it is lost.
  *saveQuoteBeforeSubmit() {
    this.disableSubmitLoanButton = true;
    const saved = yield this.saveQuote();
    if (!saved) {
      this.disableSubmitLoanButton = false;
    }
    return saved;
  }

  *saveSuggestedQuoteIfEligible(calculatedFields: Record<string, number>) {
    if (this.globalStore.isInternalPortal) return;
    if (this.isManualLeverageRequired(calculatedFields.totalLoanAmount)) return;
    if (this.loanEconomicsStore.instaQuoteRanges?.eligible === false) return;

    try {
      yield this.oneToolSevice.saveSuggestedQuote(this.quoteId, calculatedFields);
    } catch (e) {
      console.log(e);
    }
  }

  *getSuggestedQuote() {
    try {
      const response = yield this.oneToolSevice.getSuggestedQuote(this.loanId);
      this.suggestedLoanQuote = response.data.data;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *checkIfLoanApplicableForIV() {
    try {
      const response = yield this.oneToolSevice.checkIfLoanApplicableForIV(this.getQuoteData());
      this.internalValuationPilotApplicable = response.data.data;
    } catch (e) {
      console.log(e)
    }
  }

  *loadQuote(quoteId) {
    try {
      this.isLoanQuoteDetailsSuccess = true;
      const data = yield this.oneToolSevice.getQuoteById(quoteId);
      const quoteData = data.data.data;

      if (!quoteData || !quoteData.quoteId) {
        throw new Error("Don't have access to the quote");
      }

      if (!quoteData.uiState.selectEntityOption) {
        this.resetSteps(quoteData);
      }

      this.getCorrectTermStep(quoteData);

      this.quoteId = quoteData.quoteId;
      this.loanId = quoteData.loanId;
      this.lenderId = quoteData?.lenderId ?? this.lenderId ?? this.globalStore?.lenderInfo?.lenderId ?? null;
      this.hasCorrespondentBeta = quoteData.hasCorrespondentBeta ?? false;
      this.summaryStore.loadLoanCommentsData(
        quoteData.loanComments,
        quoteData.leverageExceptionComments
      );
      this.borrowersStore.loadBorrowers(quoteData.borrowers);
      this.propertiesStore.loadProperties(quoteData);
      this.closingStore.loadClosingData(quoteData);
      this.loanEconomicsStore.loadLoanEconomics(quoteData.loanEconomics, quoteData.isHighLeverageFico);
      this.brokerStore.loadBroker(quoteData.broker);
      this.entityStore.loadEntityData(quoteData);
      this.preflightStore.loadPreflightState(quoteData);
      this.pricerSummaryStore.loadPricerData(quoteData);
      this.step = quoteData.uiState.quoteStep;
      this.loadInternalValuationValues(quoteData);

      const totalPurchasePrice = sum(this.propertiesStore.getProperties().map(property => property.purchasePrice || 0));

      if (totalPurchasePrice > 0 && this.isBridgeLoan) {
        yield this.loanEconomicsStore.fetchPreviousInstaQuoteRanges();
      }

      if (quoteData.fastTrackLoanId) {
        this.fastTrackLoanId = quoteData.fastTrackLoanId;
        yield this.getLoanFastTrackPaymentUrl();
      }
      yield this.loanEconomicsStore.fetchQuoteOutputs();
      if (this.loanSubtype === LoanSubType.RENTAL_PORTFOLIO) {
        yield this.portfolioInformationFormStore.fetchStateCounties();
        this.portfolioInformationFormStore.loadPortfolioInformation(quoteData);
      }
    } catch (e) {
      console.log(e);
      this.isLoanQuoteDetailsSuccess = false;
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  loadInternalValuationValues = (quoteData) => {
    this.internalValuationPilotSelected = quoteData.internalValuationPilotSelected;
    this.internalValuationPilotApplicable = quoteData.internalValuationPilotApplicable || quoteData.internalValuationPilotSelected;
    this.internalValuationPilotFeeName = quoteData.internalValuationPilotFeeName;
  }

  getCorrectTermStep(quoteData) {
    if (this.loanSubtype === LoanSubType.SINGLE_PROPERTY) {
      if (quoteData.uiState.quoteStep === QuoteStep.SUMMARY || quoteData.uiState.quoteStep === QuoteStep.PREFLIGHT || quoteData.uiState.quoteStep === QuoteStep.LOAN_PRICER) {
        quoteData.uiState.quoteStep = QuoteStep.PROPERTY_INFORMATION;
        quoteData.uiState.preflightStep = PreflightStep.BROKER;
        quoteData.uiState.preflightNextStep = PreflightStep.BROKER;
      }
    }
    if (this.loanSubtype === LoanSubType.RENTAL_PORTFOLIO) {
      if (quoteData.uiState.quoteStep === QuoteStep.SUMMARY || quoteData.uiState.quoteStep === QuoteStep.PREFLIGHT || quoteData.uiState.quoteStep === QuoteStep.LOAN_PRICER) {
        quoteData.uiState.quoteStep = QuoteStep.PORTFOLIO_INFORMATION;
        quoteData.uiState.preflightStep = PreflightStep.BROKER;
        quoteData.uiState.preflightNextStep = PreflightStep.BROKER;
      }
    }
  }

  resetSteps(quoteData) {
    if (quoteData.entity?.entityId) {
      quoteData.uiState.selectEntityOption = SelectEntityOption.SEARCH_ENTITY;
      quoteData.uiState.quoteStep = QuoteStep.BORROWER_INFORMATION;
    } else if (quoteData.entity?.entityEin) {
      quoteData.uiState.selectEntityOption = SelectEntityOption.ADD_ENTITY;
      quoteData.uiState.quoteStep = QuoteStep.ENTITY_INFORMATION;
    } else if (quoteData.borrowers?.[0]?.firstName) {
      quoteData.uiState.selectEntityOption = SelectEntityOption.NO_ENTITY;
      quoteData.uiState.quoteStep = QuoteStep.BORROWER_INFORMATION;
    } else {
      quoteData.uiState.selectEntityOption = null;
      quoteData.uiState.quoteStep = QuoteStep.ENTITY_INFORMATION;
    }
  }

  *createNewQuote() {
    try {
      yield this.loanEconomicsStore.fetchDefaultFees();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *doSubmitLoanValidation() {
    if (this.loanSubtype === LoanSubType.SINGLE_PROPERTY) {
      return true;
    }

    if (this.loanSubtype === LoanSubType.RENTAL_PORTFOLIO) {
      if (this.preflightStore.validateRentalProperties(true)) {
        return true
      }
      return false;
    }

    this.checkAllErrors();
    this.checkAllWarnings();

    const getSubmitBridgeLoanRequest_apiResponse = yield this.oneToolSevice.getSubmitBridgeLoanRequest(
      this.getQuoteData()
    );
    const submitBridgeLoanRequestData =
      getSubmitBridgeLoanRequest_apiResponse.data.data;
    const quoteData = this.getQuoteData();

    yield this.validateBridgeSubmission(submitBridgeLoanRequestData);
    yield this.validateBorrowerErrors(quoteData, submitBridgeLoanRequestData);
    yield this.validateEntityErrors(submitBridgeLoanRequestData);
    yield this.getAreBorrowersPreQualifiedFromPrescreenValidations(
      submitBridgeLoanRequestData
    );
    this.validateProtectedBorrowers(quoteData);

    if (this.allErrors?.length == 0) {
      return true;
    } else {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Please correct the errors listed above.',
      });
      return false;
    }
  }

  *submitLoan() {
    try {
      const validationSuccessful = yield this.doSubmitLoanValidation();
      if (validationSuccessful) {
        const quoteSaved = yield this.saveQuoteBeforeSubmit();
        if (!quoteSaved) return;
        const response = yield this.oneToolSevice.submitLoan(
          this.getQuoteData()
        );
        this.submittedLoan = response.data.data;
        this.step = QuoteStep.SUCCESS;
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      this.sendLoanSubmissionErrorEmail(
        e?.error?.response?.data?.error?.message || e?.error?.message || e
      );
    }
  }

  *submitLoanApplication() {
    try {
      const validationSuccessful = yield this.doSubmitLoanValidation();
      if (validationSuccessful) {
        const quoteSaved = yield this.saveQuoteBeforeSubmit();
        if (!quoteSaved) return;
        const response = yield this.oneToolSevice.submitLoanApplication(
          this.getQuoteData()
        );
        this.submittedLoan = response.data.data;
        this.step = QuoteStep.SUCCESS;
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      this.sendLoanSubmissionErrorEmail(
        e?.error?.response?.data?.error?.message || e?.error?.message || e
      );
    }
  }

  sendLoanSubmissionErrorEmail(error: string) {
    if (this.loanSubtype === LoanSubType.RENTAL_PORTFOLIO || this.loanSubtype === LoanSubType.SINGLE_PROPERTY) {
      this.oneToolSevice.sendErrorEmail(
        'Term Loan Submission Failed',
        `Error occured while submitting ${LoanType.RESIDENTIAL_TERM} (${this.loanSubtype}) loan`,
        error || 'Error occured when submitting loan'
      );
    } else {
      this.oneToolSevice.sendErrorEmail(
        'Bridge Loan Submission Failed',
        `Error occured while submitting ${LoanType.RESIDENTIAL_BRIDGE} (${this.loanSubtype}) loan`,
        error || 'Error occured when submitting loan'
      );
    }
  }

  sendUpdateLoanSubmissionErrorEmail(error: string) {
    this.oneToolSevice.sendErrorEmail(
      'Update Loan Failed',
      `Error occured while updating (${this.loanSubtype}) loan with id (${this.loanId})`,
      error || 'Error occured when updating loan'
    );
  }

  getQuoteData() {
    const isTermLoan = this.loanSubtype === LoanSubType.RENTAL_PORTFOLIO || this.loanSubtype === LoanSubType.SINGLE_PROPERTY
    const closingData = this.closingStore.getClosingData();
    const summaryData = this.summaryStore.loanCommentsFormStore.getFormValues();
    let propertyData = this.propertiesStore.getProperties()
    if (this.secondAppraisal.secondAppraisalRequired) {
      propertyData = propertyData.map(propertyData => {
        return {
          ...propertyData,
          ...this.secondAppraisal,
        }
      })
    }
    if (propertyData.length > 0 && propertyData[0].ltcCatchupDraw) {
      propertyData = propertyData.map(propertyData => {
        return {
          ...propertyData,
          ltcCatchupDrawAmount: this.loanEconomicsStore.calculatedFields.ltcCatchupDrawAmount,
        }
      })
    }
    const secondAppraisalValues = this.preflightStore.secondAppraisalFormStore.getFormValues()
    const brokerData = this.brokerStore.getBroker();
    const loanEconomicsData = this.loanEconomicsStore.getLoanEconomics();
    const loanPortfolioInformationData = this.portfolioInformationFormStore.getLoanPortfolioInformation();
    const lenderId = this.globalStore.lenderInfo?.lenderId ?? this.lenderId;
    const isInternal = this.globalStore.lenderInfo?.isInternal ?? false;

    const brokerPoints = brokerData.brokerPoints;
    loanEconomicsData.brokerPoints = brokerPoints;
    const isFFHighFicoQuote = this.loanEconomicsStore?.meetsAllHighFicoCriteria
    let borrowers = this.borrowersStore.getBorrowers()
    if (isFFHighFicoQuote) {
      borrowers = borrowers.map(borrower => {
        return {
          ...borrower,
          personalGuarantor: true,
        };
      });
    }

    return {
      quoteId: this.quoteId,
      loanId: this.loanId,
      lenderId,
      isInternal,
      hasCorrespondentBeta: this.isCorrespondentBetaFormEnabled,
      loanSubtype: this.loanSubtype,
      originatorId: closingData.originatorId,
      version: 1,
      broker: brokerData,
      entity: this.entityStore.getEntity(),
      closingData: this.closingStore.getClosingData(),
      quoteAccepted: this.quoteAccepted,
      borrowers,
      properties: propertyData,
      loanEconomics: loanEconomicsData,
      isHighLeverageFico: isFFHighFicoQuote,
      uiState: {
        quoteStep: this.step,
        ...this.preflightStore.getPrefligthState(),
        uploadedLicenses: this.propertiesStore.uploadedLicenses,
        selectEntityOption: this.entityStore.selectEntityOption,
        displayBrokerFee: brokerData.displayBrokerFee
      },
      loanLenderDetails: isTermLoan ? this.pricerSummaryStore.loanLenderDetails : this.loanEconomicsStore.loanLenderDetails,
      loanComments: summaryData.loanComments,
      leverageExceptionComments: summaryData.leverageExceptionComments,
      ...this.getFastTrackData(closingData),
      portfolioInformation: loanPortfolioInformationData,
      customQuotes: JSON.stringify(this.pricerSummaryStore.customQuotes || []),
      secondAppraisal: ONE_PAYMENT,
      internalValuationPilotSelected: this.internalValuationPilotSelected && this.internalValuationPilotApplicable,
      internalValuationPilotApplicable: this.internalValuationPilotApplicable,
      internalValuationPilotFeeName: this.internalValuationPilotApplicable ? this.internalValuationPilotFeeName : null,
    };
  }

  getLeverageMatrixData() {
    let propertyData = this.propertiesStore.getProperties();
    if (this.secondAppraisal.secondAppraisalRequired) {
      propertyData = propertyData.map(propertyData => {
        return {
          ...propertyData,
          ...this.secondAppraisal,
        }
      })
    }
    if (propertyData.length > 0 && propertyData[0].ltcCatchupDraw) {
      propertyData = propertyData.map(propertyData => {
        return {
          ...propertyData,
          ltcCatchupDrawAmount: this.loanEconomicsStore.calculatedFields.ltcCatchupDrawAmount,
        }
      })
    }

    let borrowers = this.borrowersStore.getBorrowers();
    const isFFHighFicoQuote = this.loanEconomicsStore?.meetsAllHighFicoCriteria;
    if (isFFHighFicoQuote) {
      borrowers = borrowers.map(borrower => {
        return {
          ...borrower,
          personalGuarantor: true,
        };
      });
    }

    const productBox = this.loanEconomicsStore.getProductBox(this.loanSubtype);

    return {
      properties: propertyData,
      borrowers,
      productBox,
    };
  }

  isManualLeverageRequired(totalLoanAmount: number) {
    const isHighFico = this.loanEconomicsStore.meetsAllHighFicoCriteria;
    const propertyCount = this.propertiesStore.propertyFormStores?.length;

    let typicalValueException = false;
    if (propertyCount === 1 && isHighFico && totalLoanAmount && totalLoanAmount >= TYPICAL_VALUE_LOWER_LIMIT && totalLoanAmount <= TYPICAL_VALUE_UPPER_LIMIT) {
      typicalValueException = this.loanEconomicsStore.arePropertiesWithinPriceRange();
    }

    return (
      (!typicalValueException && totalLoanAmount > 1000000) || // all loan types
      !this.loanEconomicsStore.isLTCAvailable() || // all loan types
      this.loanEconomicsStore.isManualQuote
    );
  }

  isMidConstructionRefinance() {
    let isRefinance = false;
    let sunkCost = 0;

    this.propertiesStore.getProperties()?.forEach(prop => {
      if (prop.loanPurpose === REFINANCE) {
        isRefinance = true;
        sunkCost += prop.constructionRenoSpendToDate || 0;
      }
    });

    return isRefinance && sunkCost > 0;
  }

  getCostBasisLabel() {
    let costBasisLabel = 'Purchase Price';
    if (this.propertiesStore.getProperties()?.[0]?.loanPurpose === REFINANCE) {
      const seasoningDays = getDaysBetween(
        new Date(),
        new Date(this.propertiesStore.getProperties()?.[0]?.purchaseDate)
      );
      if (seasoningDays <= 180) {
        // <= 180 days seasoning
        costBasisLabel = 'Cost Basis';
      }
    }
    return costBasisLabel;
  }

  get showOnlyLTVValues() {
    if (this.propertiesStore.getProperties()?.[0]?.loanPurpose === REFINANCE) {
      return this.isSeasoningDaysGreaterThan180;
    }
    return false;
  }

  get isSeasoningDaysGreaterThan180() {
    const properties = this.propertiesStore.getProperties();
    const seasoningDays = getDaysBetween(
      new Date(),
      new Date(properties?.[0]?.purchaseDate)
    );
    return seasoningDays > 180;
  }

  * loadFastTrackFees() {
    try {
      const response = yield this.oneToolSevice.getLoanFastTrackFees();

      this.fastTrackFees = response.data.data;
    } catch (err) { }
  }

  * fetchFastTrackLoanId() {
    try {
      if (!this.fastTrackLoanId) {
        const apiResponse: ApiResponse = yield this.oneToolSevice.fetchFastTrackLoanId();

        this.fastTrackLoanId = apiResponse.data.data;
      }
    } catch (error) {
      // do nothing
    }
  }

  * getLoanFastTrackPaymentUrl() {
    try {
      const response = yield this.oneToolSevice.getLoanFastTrackPaymentUrl(
        this.fastTrackLoanId
      );

      this.fastTrackPaymentUrl = response.data.data.url;
    } catch (err) { }
  }

  * updateLoanFastTrackInfo(fastTrackOptions: any, loanId?: string | number) {
    try {
      const borrowers = this.borrowersStore.getBorrowers();
      const properties = this.propertiesStore.getProperties();

      const data = {
        loanId: loanId,
        randomLoanId: this.fastTrackLoanId,
        ...fastTrackOptions,
        properties: properties.length,
        propertiesAddresses: properties
          .map(property => buildAddress(property))
          .join(', '),
        dealName: getDealName(borrowers, properties),
      };
      yield this.oneToolSevice.updateLoanFastrackInfo(data);
      // The link is for the saved Fast Track, so it can only be fetched once it exists.
      yield this.getLoanFastTrackPaymentUrl();
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while saving the modal state',
      });
    }
  }

  getFastTrackData(closingData) {
    const { fastTrack, preferredInsurance } = closingData;

    if (!fastTrack) {
      return { fastTrack };
    }

    const isElmsure = preferredInsurance === ELMSURE;
    const properties = this.propertiesStore.getProperties();

    return {
      fastTrack,
      fastTrackLoanId: this.fastTrackLoanId,
      fastTrackOptions: {
        insuranceReview: !isElmsure,
        titleSearch: false,
        insurance: isElmsure,
        rushedAppraisal: true,
        titleAttorneySearch: true,
        properties: properties.length,
        propertiesAddresses: properties
          .map(property => buildAddress(property))
          .join(', '),
      },
    };
  }

  * requestFastTrackPaymentEmail() {
    try {
      yield this.oneToolSevice.requestFastTrackPaymentEmail({
        loanId: this.fastTrackLoanId,
        borrowers: [...this.borrowersStore.getBorrowers()],
      });
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Email sent successfully',
      });
      // The email issued a new link and ended the one on screen; show the emailed one.
      yield this.getLoanFastTrackPaymentUrl();
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while sending the email',
      });
    }
  }

  validateLeverageStep() {
    let errors = [];
    errors = errors.concat([
      ...this.loanEconomicsStore.validateLeverageAmounts(),
    ]);

    errors = errors.concat([...this.loanEconomicsStore.validateLoanAmount()]);

    this.stepErrors = errors;
    return errors.length === 0;
  }

  getNumberOfUnits = () => {
    const { isSingleProperty } = this.pricerStore;
    const formValues = this.pricerStore.getFormValues();

    if (isSingleProperty) {
      return formValues['propertyType'] == MIXED_USE
        ? Number(formValues['residentialUnits']) +
        Number(formValues['commercialUnits'])
        : formValues['units'];
    }

    return '';
  };

  getNumberOfProperties = () => {
    const { isSingleProperty } = this.pricerStore;
    if (isSingleProperty) {
      return 1;
    } else {
      return this.portfolioInformationFormStore.getProperties()
        .map(x => x.numberOfProperties || 0)
        .reduce((sum, current) => sum + Number(current), 0);
    }
  };

  getPropertyTypes() {
    return this.propertiesStore.getProperties().filter(x => x.propertyType).map(x => x.propertyType);
  };

  getPropertyRentalPortfolioTypes() {
    return this.portfolioInformationFormStore.getProperties().filter(x => x.propertyType).map(x => x.propertyType);
  };

  getRateType(rateType, amortization) {
    return amortization.value == "fullyAmortizing" ? rateType.value + " Fully Amortizing" : rateType.value;
  }

  getPropertyDataPortfolio = () => {
    let propertyData = [];
    let mixedUseProperties = [];
    let multifamilyProperties = [];

    this.portfolioInformationFormStore.getProperties().map(p => {
      if (p.propertyType === MULTIFAMILY_5_PLUS) {
        multifamilyProperties = p.mfProperties.map(
          mfProperty => mfProperty.numberOfUnits
        );
      }
      if (p.propertyType === MIXED_USE) {
        p.muProperties.forEach(mp => {
          mixedUseProperties.push({
            ...mp,
            commercialUnitsCurrentOnRent:
              mp.commercialUnitsCurrentOnRent === 'Y',
          });
        });
      }
      propertyData.push({
        ...p,
        noOfProperties: p.numberOfProperties,
        propertyType: p.propertyType,
        mixedUseProperties,
        estimatedAsIsValue: p.estimatedAsIsValue,
        multifamilyProperties,
      });
    });

    return propertyData;
  };

  getPropertyCounties() {
    const { propertyCounties } = this.portfolioInformationFormStore.form.fields;

    return propertyCounties.value
      ? propertyCounties.value.map(x => x.value)
      : [];
  }

  getPropertyTypesAsString = () => {
    return this.propertiesStore.getProperties()
      .filter(x => x.propertyType)
      .map(x => x.propertyType)
      .join(',');
  };

  getRentalPortfoliosPropertyTypesAsString = () => {
    return this.portfolioInformationFormStore.getProperties()
      .filter(x => x.propertyType)
      .map(x => x.propertyType)
      .join(',');
  };

  isValidBankruptcyDate(bankruptcyDate) {
    return (
      bankruptcyDate &&
      differenceInYears(startOfToday(), new Date(bankruptcyDate)) >= 3
    );
  }

  isValidForeclosureDate(foreclosureDate) {
    return (
      foreclosureDate &&
      differenceInYears(startOfToday(), new Date(foreclosureDate)) >= 3
    );
  }

  * fetchStateCounties() {
    try {
      const response: ApiResponse = yield this.oneToolSevice.getStateCounties();
      this.allCounties = response.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting state counties.',
      });
    }
  }

  * isSecondAppraisalNeeded() {
    try {
      const pricerLoanTerms = this.pricerSummaryStore.pricerLoanTerms;
      const propertyFormData = this.propertiesStore.getProperties();
      const secondAppraisalData: IsSecondAppraisalRequiredRequest = {
        acquisitionPrice: propertyFormData[0].purchasePrice,
        asIsValue: propertyFormData[0].currentMarketValue,
        loanPurpose: this.pricerStore.getLoanPurpose(),
        loanSubtype: this.loanSubtype,
        capitalImprovements: propertyFormData[0]?.constructionRenoSpendToDate,
        loanType: LoanType.RESIDENTIAL_TERM,
        loanAmount: pricerLoanTerms?.amount,
        numberOfUnits: mapNumberOfUnits(propertyFormData[0]?.propertyType),
        purchaseDate: propertyFormData[0]?.purchaseDate,
        state: propertyFormData[0]?.state
      }
      const response: ApiResponse = yield this.oneToolSevice.isSecondAppraisalNeeded(secondAppraisalData);
      this.secondAppraisal = response.data?.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting second appraisal information.',
      });
    }
  }

  get counties() {
    const propertyFormFields = this.propertiesStore.propertyFormStores.length > 0 ? this.propertiesStore.propertyFormStores[0].form.fields : '';
    const states = propertyFormFields.state.value
      ? [propertyFormFields.state.value]
      : [];
    let selectedStateCounties = [];
    if (states && states.length > 0) {
      selectedStateCounties = states.reduce(
        (c, s) => [...c, ...(this.allCounties[s] ?? [])],
        []
      );
    }

    const counties = [];
    if (selectedStateCounties && selectedStateCounties.length > 0) {
      selectedStateCounties.sort().map(county => {
        const countyParts = county.split(',');
        counties.push({ value: countyParts[0].trim(), label: county });
      });
    }
    return counties;
  }

  getLoanQuoteData(): LoanQuoteData {
    const borrowerFields = this.borrowersStore.borrowerFormStores[0].getFormValues();

    const borrowersExperience = borrowerExpMapping[borrowerFields.numberOfVerifiedInvestmentProperties];
    const estimatedCreditScore = borrowerFields.ficoScore;
    const citizenshipStatus = borrowerFields.citizenshipStatus;

    return {
      borrowersExperience,
      estimatedCreditScore,
      citizenshipStatus
    };
  }

  * loadQuoteToEdit(loanId: string, isManualQuote: boolean = false, lastFetchedCustomQuote?: CustomQuoteRequest) {
    try {
      this.reset();
      const data = yield this.oneToolSevice.getLoanAsQuoteById(+loanId);
      const quoteData = data.data.data;

      this.quoteId = quoteData.quoteId;
      this.loanId = quoteData.loanId;
      this.lenderId = quoteData?.lenderId ?? this.lenderId ?? this.globalStore?.lenderInfo?.lenderId ?? null;
      this.hasCorrespondentBeta = quoteData.hasCorrespondentBeta ?? false;
      this.isEditingLoan = true;

      this.summaryStore.loadLoanCommentsData(
        quoteData.loanComments,
        quoteData.leverageExceptionComments
      );
      this.borrowersStore.loadBorrowers(quoteData.borrowers);
      this.propertiesStore.loadProperties(quoteData);
      this.loanEconomicsStore.loadFees(quoteData?.loanEconomics?.fees);
      this.closingStore.loadClosingData(quoteData);
      this.loanEconomicsStore.loadLoanEconomics(quoteData.loanEconomics, false);
      this.brokerStore.loadBrokerData(quoteData);
      this.entityStore.loadEntityData(quoteData);
      this.preflightStore.loadPreflightState(quoteData);
      this.step = quoteData.uiState.quoteStep;

      const totalPurchasePrice = sum(this.propertiesStore.getProperties().map(property => property.purchasePrice || 0));

      if (totalPurchasePrice > 0 && this.isBridgeLoan) {
        yield this.loanEconomicsStore.fetchPreviousInstaQuoteRanges();
      }

      if (isManualQuote && lastFetchedCustomQuote) {
        this.loadManualQuoteData(lastFetchedCustomQuote)
      }
    } catch (e) {
      console.log(e);
      this.isLoanQuoteDetailsSuccess = false;
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *loadManualQuoteData(lastFetchedCustomQuote: CustomQuoteRequest) {
    try {
      this.loanEconomicsStore.loadFees(lastFetchedCustomQuote?.fees)
      this.loanEconomicsStore.loanEconomicsFormStore.onFieldChange("loanTermMonths", lastFetchedCustomQuote?.loanTermMonths)
      this.loanEconomicsStore.loanEconomicsFormStore.onFieldChange("interestRate", lastFetchedCustomQuote?.interestRate)
      this.loanEconomicsStore.manualExceptionQuoteFormStore.loadForm({
        loanToCost: lastFetchedCustomQuote?.loanToCost,
        constructionReserve: lastFetchedCustomQuote?.constructionReserve,
      });
    } catch (e) {
      console.log(e);
      this.isLoanQuoteDetailsSuccess = false;
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  * updateLoan(successCallBack: () => void) {
    try {
      const validationSuccessful = yield this.doSubmitLoanValidation();
      if (validationSuccessful) {
        this.disableSubmitLoanButton = true;
        const response = yield this.oneToolSevice.updateLoan(
          this.getQuoteData()
        );
        if (!response?.data?.data?.success) {
          throw new Error(response?.data?.data?.responseMessage);
        }

        successCallBack && successCallBack();
        this.globalStore.notificationStore.showSuccessNotification({
          message: "Loan Updated Successfully."
        });
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      this.sendUpdateLoanSubmissionErrorEmail(
        e?.error?.response?.data?.error?.message || e?.error?.message || e?.message || e
      );
      this.disableSubmitLoanButton = false;
    }
  }

  *updateManualQuoteLoan(successCallBack: () => void) {
    try {
      this.disableSubmitLoanButton = true;
      const response = yield this.oneToolSevice.updateLoan(
        this.getQuoteData()
      );
      if (!response?.data?.data?.success) {
        throw new Error(response?.data?.data?.responseMessage);
      }

      successCallBack && successCallBack();
      this.globalStore.notificationStore.showSuccessNotification({
        message: "Manual Quote Updated Successfully."
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      this.sendUpdateLoanSubmissionErrorEmail(
        e?.error?.response?.data?.error?.message || e?.error?.message || e?.message || e
      );
      this.disableSubmitLoanButton = false;
    }
  }

  get allowPricerStep() {
    const isBroker = this.globalStore.userFeatures.isBroker;
    const allowLoanPricing = this.userStore.allowLoanPricing;
    if (LoanSubType.SINGLE_PROPERTY === this.loanSubtype) {
      return isBroker || allowLoanPricing;
    } else {
      return allowLoanPricing;
    }

  }

  *saveLoanTermApprovalValue() {
    try {
      const response = yield this.oneToolSevice.saveLoanTermApprovalValue(this.loanId);
    } catch (e) {
      console.log(e);
    }
  }
}
